import {
  Avatar,
  Box,
  Button,
  Center,
  Group,
  PasswordInput,
  Select,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import React, { useEffect, useState } from "react";
import { z } from "zod";
import { Check, ExclamationMark } from "tabler-icons-react";

import { getInitials } from "utils/getInitials";
import { formatRoleName } from "utils/formatRoleName";
import { useAuth } from "api/auth";
import { showNotification } from "@mantine/notifications";
import axios from "axios";
import { useGetDepartments } from "api/department/getAllDepartment";
import { useGetProgramsByDepartment } from "api/program/getProgramsByDepartment";
import { useGetRolePermissions } from "api/role/getRoles";

type AddOrgMembersFormProps = {
  onClose: () => void;
  orgId: string;
};

const addOrgMemberSchema = z.object({
  firstName: z
    .string()
    .trim()
    .min(1, { message: "Please enter your first name" }),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  lastName: z
    .string()
    .trim()
    .min(1, { message: "Please enter your last name" }),
  email: z
    .string()
    .email({ message: "Invalid email address" })
    .min(1, { message: "Please enter your email address" }),
  password: z
    .string()
    .min(6, { message: "Password must contain at least 6 characters" }),
  position: z.string().trim().min(1, { message: "Please enter your position" }),
  role: z.string().trim().min(1, { message: "Please select member role" }),
  department: z.string().optional(),
  program: z.string().optional(),
  org: z.string().trim().min(1, { message: "Organization not found" }),
  employmentType: z.enum(
    ["full-time", "seasonal", "contractor", "probation", "hourly", "term"],
    { errorMap: () => ({ message: "Please select employment type" }) }
  ),
});

function AddOrgMembersForm({ onClose, orgId }: AddOrgMembersFormProps) {
  const { createUser } = useAuth();
  const { data } = useGetDepartments();
  const { data: rolesData } = useGetRolePermissions();
  const [selectedDepartment, setSelectedDepartment] = useState<string | null>(null);
  const { data: programsData } = useGetProgramsByDepartment(selectedDepartment);

  const form = useForm({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      role: "",
      position: "",
      org: orgId,
      department: "",
      program: "",
      startDate: "",
      endDate: "",
      employmentType: "full-time",
    },
    validate: zodResolver(addOrgMemberSchema),
  });
  const [initials, setInitials] = useState(
    getInitials(form.values.firstName, form.values.lastName)
  );
  const departmentsRaw = data?.data ?? data ?? [];
  const departments: any[] = Array.isArray(departmentsRaw)
    ? departmentsRaw
    : [];
  const source = departments;

  const departmentOptions =
    source?.map((d) => ({
      value: d._id as string,
      label: d.name as string,
    })) ?? [];

  const programsRaw = programsData?.data ?? [];
  const programs: any[] = Array.isArray(programsRaw) ? programsRaw : [];
  const programOptions =
    programs?.map((p) => ({
      value: p._id as string,
      label: p.name as string,
    })) ?? [];

  // Get dynamic roles from organization
  const rolesRaw = rolesData?.data ?? [];
  const roleOptions =
    rolesRaw?.map((r: any) => ({
      value: r.role as string,
      label: formatRoleName(r.role as string),
    })) ?? [];

  const hasPrograms = programs && programs.length > 0;

  const handleDepartmentChange = (value: string | null) => {
    form.setFieldValue("department", value || "");
    setSelectedDepartment(value);
    // Reset program field when department changes
    form.setFieldValue("program", "");
  };

  useEffect(() => {
    setInitials(getInitials(form.values.firstName, form.values.lastName));
  }, [form.values.firstName, form.values.lastName]);

  type FormValues = typeof form.values;

  const handleSubmit = (values: FormValues) => {
    createUser(values, {
      onSuccess: () => {
        showNotification({
          title: "Success",
          message: `User added in Organization`,
          color: "teal",
          icon: <Check />,
        });
        onClose();
      },
      onError: (err) => {
        if (axios.isAxiosError(err)) {
          if (err.response?.status === 409) {
            form.setFieldError(
              "email",
              "An account with this email already exists"
            );
            return;
          }
        }
        showNotification({
          title: "Server Error",
          message: "Please try again later",
          color: "red",
          icon: <ExclamationMark />,
        });
      },
    });
  };
  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack>
        <Center>
          <Avatar color="violet.7" size={80} radius={40}>
            {initials}
          </Avatar>
        </Center>
        <TextInput
          label="First Name"
          withAsterisk
          {...form.getInputProps("firstName")}
        />
        <TextInput
          label="Last Name"
          withAsterisk
          {...form.getInputProps("lastName")}
        />
        <TextInput
          label="Email"
          withAsterisk
          inputWrapperOrder={["label", "input", "description", "error"]}
          {...form.getInputProps("email")}
        />
        <PasswordInput
          label="Password"
          description={
            form.errors.password || form.isValid("password")
              ? null
              : "Password must contain at least 6 characters"
          }
          inputWrapperOrder={["label", "input", "description", "error"]}
          {...form.getInputProps("password")}
        />
        {form.isValid("password") && (
          <Text
            size={12}
            color="teal.9"
            sx={{ display: "flex", alignItems: "center" }}
          >
            <Check size={14} />
            <Box ml={5}>Password must contain at least 6 characters</Box>
          </Text>
        )}
        <TextInput
          label="Position"
          withAsterisk
          {...form.getInputProps("position")}
        />
        <Select
          label="Department"
          data={departmentOptions}
          size="md"
          searchable
          value={form.values.department || ""}
          onChange={handleDepartmentChange}
        />
        {hasPrograms && (
          <Select
            label="Program"
            data={programOptions}
            size="md"
            searchable
            {...form.getInputProps("program")}
          />
        )}
        <Select
          withAsterisk
          label="Role"
          placeholder="Select a role"
          searchable
          data={roleOptions}
          size="md"
          {...form.getInputProps("role")}
        />
        <Select
          withAsterisk
          label="Employment Type"
          placeholder="Select employment type"
          data={[
            { value: "full-time", label: "Full-Time" },
            { value: "seasonal", label: "Seasonal" },
            { value: "contractor", label: "Contractor" },
            { value: "probation", label: "Probation" },
            { value: "hourly", label: "Hourly" },
            { value: "term", label: "Term" },
          ]}
          mb={20}
          {...form.getInputProps("employmentType")}
        />

        <Group grow>
          <TextInput
            type="date"
            label="Start Date"
            {...form.getInputProps("startDate")}
          />
          <TextInput
            type="date"
            label="End Date"
            placeholder="Select end date"
            {...form.getInputProps("endDate")}
          />
        </Group>

        <Group position="right" spacing={20} mt={10}>
          <Button color="cancel" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="filled" disabled={!form.isDirty()}>
            Add
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

export default AddOrgMembersForm;
